import { useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useDataSource } from '@/src/renderer/lib/dataSource'
import type { Task } from '@/src/renderer/lib/db/models'
import { showUndoToast } from '@/src/renderer/components/common/UndoToast'
import { DormantIcon } from '@/src/renderer/components/icons'
import { nextTaskAfterLeaving } from '../utils/nextTaskAfterLeaving'

interface UseRestFromDetailsParams {
  /** Die flache Navigationsliste der Seite — daran wird der Nachbar gesucht. */
  navList: ReadonlyArray<{ type: string; id: string }>
  /** Rückt die Auswahl auf diese Aufgabe (`navigation.selectTask`). */
  selectTask: (taskId: string) => unknown
  /** Der optimistische Kanal der Seite — die Änderung steht im nächsten Bild. */
  setOptimisticTaskOverride: (taskId: string, partial: Partial<Task>) => void
}

/**
 * **„Ruhen lassen" aus den Aufgaben-Details** (entschieden 2026-09-19).
 *
 * Vier Dinge, in DIESER Reihenfolge:
 *
 * 1. **Die wachen bestimmen.** Bei gemischter Auswahl legt die Zeile genau die
 *    schlafen (CONTEXT.md). Eine schon ruhende bekäme sonst einen frischen
 *    Stempel und rutschte auf der Seite **Ruhend** nach oben, obwohl an ihr
 *    nichts geschehen ist. Deshalb geht der Weg hier NICHT über
 *    `useContextMenuBadgeUpdate`, das auf die ganze Auswahl schreibt.
 * 2. **Den Nachbarn suchen** — auf der noch vollständigen Liste. Im Stapel
 *    verschwindet eine ruhende Aufgabe sofort; danach zeigte jeder Index auf
 *    etwas anderes.
 * 3. **Die Auswahl weiterrücken.** Die Aufgaben-Details gehen nicht zu, sie
 *    zeigen die nächste — derselbe Griff wie die Pfeile in ihrem Kopf. Kommt
 *    keine mehr, bleibt die Auswahl stehen.
 * 4. **Schreiben**, danach der Rückgängig-Hinweis. Er weckt nur, was DIESER
 *    Griff gelegt hat.
 *
 * Das Datum bleibt stehen: Geschrieben wird allein `pausedAt`.
 */
export function useRestFromDetails({
  navList, selectTask, setOptimisticTaskOverride,
}: UseRestFromDetailsParams) {
  const { t } = useTranslation()
  const source = useDataSource()

  // Als Ref, damit der zurückgegebene Griff stabil bleibt — die Liste ändert
  // sich bei jeder Bewegung, der Griff hängt als Prop an den Aufgaben-Details.
  const navListRef = useRef(navList)
  navListRef.current = navList

  return useCallback(async (tasks: readonly Task[]) => {
    const ids = tasks.filter(task => task.pausedAt == null).map(task => task.id)
    if (ids.length === 0) return

    const nextId = nextTaskAfterLeaving(navListRef.current, new Set(tasks.map(task => task.id)))
    if (nextId) selectTask(nextId)

    const write = async (pausedAt: Date | null) => {
      for (const id of ids) setOptimisticTaskOverride(id, { pausedAt })
      try {
        await source.updateTasks(ids.map(id => ({ id, changes: { pausedAt } })))
      } catch (err) {
        console.error('[TaskDetails] Failed to rest tasks:', err)
      }
    }

    await write(new Date())
    showUndoToast({
      message: t('taskItem.pausedToast', { count: ids.length }),
      description: t('taskItem.pausedToastText', { count: ids.length }),
      undoLabel: t('common.undo'),
      undoneMessage: ids.length === 1
        ? t('undo.restored.rest')
        : t('undo.restored.restMultiple', { count: ids.length }),
      icon: <DormantIcon size={18} />,
      tone: 'rest',
      onUndo: () => write(null),
    })
  }, [selectTask, setOptimisticTaskOverride, source, t])
}
